import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Tag } from 'antd';
import { getApiUrl } from '../utils/apiUrl';
import BillingRules from '../components/BillingRules';
import { displayModelId } from '../utils/modelDisplay';

const Pricing = () => {
  const [lang, setLang] = useState(() => localStorage.getItem('lang') || 'en');
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [footerCopyright, setFooterCopyright] = useState('Copyright © 2026 Blue API, All rights reserved.');

  const t = (zh, en) => (lang === 'zh' ? zh : en);

  useEffect(() => {
    const handleStorageChange = () => {
      setLang(localStorage.getItem('lang') || 'en');
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // 获取模型列表
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await fetch(getApiUrl('/api/models'));
        const data = await response.json();
        if (data.code === 0) {
          setModels(data.data.list || []);
        }
      } catch (error) {
        console.log('获取模型列表失败:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchModels();
  }, []);

  // 获取版权信息
  useEffect(() => {
    const fetchCopyright = async () => {
      try {
        const response = await fetch(getApiUrl('/api/homepage-config'));
        const data = await response.json();
        if (data.code === 0 && data.data && data.data.footer_copyright) {
          setFooterCopyright(data.data.footer_copyright);
        }
      } catch (error) {
        console.log('获取版权信息失败:', error);
      }
    };

    fetchCopyright();
  }, []);

  // 按类型分组
  const groupedModels = models.reduce((acc, model) => {
    const type = model.type || 'chat';
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(model);
    return acc;
  }, {});

  // 定价规则解析与展示辅助（metadata.pricing_rules：分时价/缓存价/阶梯价）
  const parseRules = (model) => {
    try {
      const meta = typeof model.metadata === 'string' ? JSON.parse(model.metadata || '{}') : (model.metadata || {});
      return Array.isArray(meta.pricing_rules) ? meta.pricing_rules : [];
    } catch (e) {
      return [];
    }
  };
  const WEEKDAY_CN = { 0: '日', 1: '一', 2: '二', 3: '三', 4: '四', 5: '五', 6: '六' };
  const fmtWeekdays = (w) => {
    if (!w || w.length === 0 || w.length === 7) return t('每天', 'daily');
    const sorted = [...w].sort((a, b) => a - b);
    if (sorted.join(',') === '1,2,3,4,5') return t('工作日', 'weekdays');
    return sorted.map(d => t('周', '') + WEEKDAY_CN[d]).join('·');
  };
  const fmtNum = (v) => {
    const s = parseFloat(v).toFixed(6);
    return s.replace(/0+$/, '').replace(/\.$/, '');
  };
  const fmtRulePrice = (v, fallback) => v == null ? (fallback != null ? fmtNum(fallback) : '-') : fmtNum(v);
  // 图片计费像素档位标签：有 max_pixels 显示「≤2.61M px」，兜底档用上一档上界显示「>2.61M px」
  const fmtPixels = (px) => {
    const m = px / 1000000;
    return (Number.isInteger(m) ? m : m.toFixed(2)) + 'M';
  };
  const pixelTierLabel = (tier, tiers, idx) => {
    if (tier.max_pixels) return `≤${fmtPixels(tier.max_pixels)} px`;
    const prev = tiers[idx - 1];
    return prev && prev.max_pixels ? `>${fmtPixels(prev.max_pixels)} px` : t('不限', 'any size');
  };
  // token 数用 K 显示（如 256000 → 256K，1,000 以下显示原值）
  const fmtTokens = (v) => {
    if (v == null) return '∞';
    if (v >= 1000) {
      const k = v / 1000;
      return (Number.isInteger(k) ? k : k.toFixed(1)) + 'K';
    }
    return String(v);
  };
  // 区间显示：单边开区间用 ≤/> 表示（如 输入 ≤256K、输入 >256K），闭区间用 a~b
  const fmtRange = (min, max) => {
    if (min == null && max == null) return '';
    if (min == null) return `≤${fmtTokens(max)}`;
    if (max == null) return `>${fmtTokens(min - 1)}`;
    return `${fmtTokens(min)}~${fmtTokens(max)}`;
  };
  const volText = (r) => {
    const parts = [];
    if (r.min_input_tokens != null || r.max_input_tokens != null) {
      parts.push(`${t('输入', 'input')} ${fmtRange(r.min_input_tokens, r.max_input_tokens)}`);
    }
    if (r.min_output_tokens != null || r.max_output_tokens != null) {
      parts.push(`${t('输出', 'output')} ${fmtRange(r.min_output_tokens, r.max_output_tokens)}`);
    }
    return parts.join(' · ');
  };
  // 时间窗展示：空闲全天规则显示「除高峰时段外」，普通全天规则显示「每天」
  const fmtWindow = (label, start, end) => {
    if (start === '00:00' && end === '24:00') {
      return (label || '').includes('空闲') ? t('除高峰时段外', 'off-peak hours only') : t('每天', 'daily');
    }
    return `${start}-${end}`;
  };
  // 完整时段描述（全天时不重复星期）
  const fmtSchedule = (r) => {
    const win = fmtWindow(r.label, r.start_time, r.end_time);
    if (win === t('每天', 'daily') || win === t('除高峰时段外', 'off-peak hours only')) return win;
    return `${fmtWeekdays(r.weekdays)} · ${win}`;
  };

  // 固定类型映射
  const TYPE_MAPPING = {
    text: { en: 'Text Models', cn: '对话模型' },
    image: { en: 'Image Models', cn: '图像模型' },
    embeddings: { en: 'Embeddings Models', cn: '嵌入模型' },
    audio: { en: 'Audio Models', cn: '语音模型' },
    video: { en: 'Video Models', cn: '视频模型' },
    rerank: { en: 'Rerank Models', cn: '重排序模型' },
    speech: { en: 'Speech Models', cn: '语音合成模型' },
    transcription: { en: 'Transcription Models', cn: '语音识别模型' },
  };

  // 获取类型显示名称（根据当前语言）
  const getTypeName = (type) => {
    const mapping = TYPE_MAPPING[type];
    return mapping ? (lang === 'en' ? mapping.en : mapping.cn) : type;
  };

  return (
    <div className="pricing-page">
      <nav className="pricing-nav">
        <div className="pricing-nav-container">
          <Link to="/" className="pricing-logo">
            <span>Blue API</span>
          </Link>
          <div className="pricing-nav-links">
            <Link to="/">{t('返回首页', 'Back to Home')}</Link>
          </div>
        </div>
      </nav>

      <main className="pricing-content">
        <div className="pricing-header">
          <h1>{t('价格明细', 'Pricing Details')}</h1>
          <p>{t('以下是各模型的 API 调用价格，实际消耗按使用量计费', 'Below are the API call prices for each model, actual consumption is billed based on usage')}</p>
        </div>

        {loading ? (
          <div className="pricing-loading">{t('加载中...', 'Loading...')}</div>
        ) : (
          <div className="pricing-groups">
            {Object.entries(groupedModels).map(([type, typeModels]) => (
              <div key={type} className="pricing-group">
                <h2>{getTypeName(type)}</h2>
                <div className="pricing-table-wrapper">
                  <table className="pricing-table">
                    <thead>
                      <tr>
                        <th>{t('模型', 'Model')}</th>
                        <th>{t('供应商', 'Provider')}</th>
                        <th>{t('计费方式', 'Billing Type')}</th>
                        <th>{t('价格', 'Price')}</th>
                        <th>{t('是否热门', 'Hot')}</th>
                        <th>{t('说明', 'Description')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {typeModels.map((model) => (
                        <tr key={model.id}>
                          <td>
                            <div className="model-name" style={{display:'flex',alignItems:'center',gap:8}}>
                              <span>{model.display_name}</span>
                              <small>{displayModelId(model.model_name)}</small>
                            </div>
                          </td>
                          <td>
                            <span className="provider-name">{model.provider?.display_name || '-'}</span>
                          </td>
                          <td>
                            {model.billing_type === 'per_token' && <Tag color="blue">{model.type === 'video' ? t('按Token (视频)', 'Per Token (Video)') : t('按Token', 'Per Token')}</Tag>}
                            {model.billing_type === 'per_call' && <Tag color="orange">{t('按次', 'Per Call')}</Tag>}
                            {model.billing_type === 'per_char' && <Tag color="purple">{t('按字符', 'Per Char')}</Tag>}
                            {model.billing_type === 'per_second' && <Tag color="cyan">{t('按秒', 'Per Second')}</Tag>}
                            {model.billing_type === 'per_image' && <Tag color="geekblue">{t('按张', 'Per Image')}</Tag>}
                            {model.billing_type === 'free' && <Tag color="green">{t('免费', 'Free')}</Tag>}
                          </td>
                          <td>
                            {model.billing_type === 'per_token' && model.type === 'video' && (() => {
                              let meta = {};
                              try { meta = typeof model.metadata === 'string' ? JSON.parse(model.metadata) : (model.metadata || {}); } catch(e) {}
                              const rp = meta.resolution_pricing;
                              const rpv = meta.resolution_pricing_with_video;
                              if (rp) {
                                return (
                                  <div className="video-pricing">
                                    {Object.entries(rp).map(([res, price]) => (
                                      <div key={res} style={{display:'flex', gap:8, alignItems:'flex-start', fontSize:13, marginBottom:4}}>
                                        <span style={{fontWeight:600, minWidth:44}}>{res}</span>
                                        <div style={{display:'flex', flexDirection:'column', lineHeight:1.5}}>
                                          <span style={{color:'#1677ff', whiteSpace:'nowrap'}}>${parseFloat(price).toFixed(2)}<small style={{color:'#999',fontSize:11}}>/M</small></span>
                                          {rpv && rpv[res] != null && (
                                            <span style={{color:'#52c41a', fontSize:12, whiteSpace:'nowrap'}}>
                                              {t('含参考视频', 'with ref video')} ${parseFloat(rpv[res]).toFixed(2)}<small style={{color:'#999',fontSize:11}}>/M</small>
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                );
                              }
                              return <span className="price">${parseFloat(model.input_price || 0).toFixed(2)}<small>/M tokens</small></span>;
                            })()}
                            {model.billing_type === 'per_token' && model.type !== 'video' && (() => {
                              const rules = parseRules(model);
                              if (rules.length === 0) {
                                return (
                                  <>
                                    <span className="price">${parseFloat(model.input_price || 0).toFixed(4)}</span>
                                    <small>/1K Input</small>
                                    <br/>
                                    <span className="price">${parseFloat(model.output_price || 0).toFixed(4)}</span>
                                    <small>/1K Output</small>
                                  </>
                                );
                              }
                              // 有定价规则：按规则逐条展示（分时/缓存/阶梯）
                              return (
                                <div className="rule-pricing">
                                  {rules.map((r, i) => (
                                    <div key={i} className="rule-pricing-item">
                                      <div className="rule-pricing-head">
                                        <span className="rule-pricing-label">{r.label || `${t('规则', 'Rule')}${i + 1}`}</span>
                                        <span className="rule-pricing-window">
                                          {' '}{fmtSchedule(r)}
                                          {r.holiday_exclude ? ` · ${t('节假日除外', 'holiday excl.')}` : ''}
                                        </span>
                                      </div>
                                      <div className="rule-pricing-prices">
                                        <span>{t('输入', 'Input')} ${fmtRulePrice(r.input_price, model.input_price)}</span>
                                        {r.cache_hit_price != null && <span className="rule-pricing-hit">{t('命中', 'Hit')} ${fmtNum(r.cache_hit_price)}</span>}
                                        {r.cache_write_price != null && <span>{t('创建5m', 'Write 5m')} ${fmtNum(r.cache_write_price)}</span>}
                                        {r.cache_write_1h_price != null && <span>{t('创建1h', 'Write 1h')} ${fmtNum(r.cache_write_1h_price)}</span>}
                                        <span>{t('输出', 'Output')} ${fmtRulePrice(r.output_price, model.output_price)}</span>
                                        {volText(r) && <span className="rule-pricing-vol"> · （{volText(r)}）</span>}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              );
                            })()}
                            {model.billing_type === 'per_call' && (
                              <span className="price">${parseFloat(model.billing_rate || 0).toFixed(4)}<small>/Call</small></span>
                            )}
                            {model.billing_type === 'per_char' && (
                              <span className="price">${parseFloat(model.billing_rate || model.input_price || 0).toFixed(6)}<small>/K Chars</small></span>
                            )}
                            {model.billing_type === 'per_second' && (() => {
                              let meta = {};
                              try { meta = typeof model.metadata === 'string' ? JSON.parse(model.metadata) : (model.metadata || {}); } catch(e) {}
                              const rp = meta.resolution_pricing;
                              if (rp && Object.keys(rp).length > 0) {
                                return (
                                  <div className="video-pricing">
                                    {Object.entries(rp).map(([res, price]) => (
                                      <div key={res} style={{display:'flex', gap:8, alignItems:'flex-start', fontSize:13, marginBottom:4}}>
                                        <span style={{fontWeight:600, minWidth:44}}>{res}</span>
                                        <span style={{color:'#1677ff', whiteSpace:'nowrap'}}>${fmtNum(price)}<small style={{color:'#999',fontSize:11}}>/{t('秒', 's')}</small></span>
                                      </div>
                                    ))}
                                  </div>
                                );
                              }
                              return <span className="price">${parseFloat(model.billing_rate || model.input_price || 0).toFixed(6)}<small>/Second</small></span>;
                            })()}
                            {model.billing_type === 'per_image' && (() => {
                              let meta = {};
                              try { meta = typeof model.metadata === 'string' ? JSON.parse(model.metadata) : (model.metadata || {}); } catch(e) {}
                              const tiers = Array.isArray(meta.pixel_pricing) ? meta.pixel_pricing : null;
                              if (tiers && tiers.length > 0) {
                                return (
                                  <div className="video-pricing">
                                    {tiers.map((tier, i) => (
                                      <div key={i} style={{display:'flex', gap:8, alignItems:'flex-start', fontSize:13, marginBottom:4}}>
                                        <span style={{fontWeight:600, minWidth:78}}>{pixelTierLabel(tier, tiers, i)}</span>
                                        <span style={{color:'#1677ff', whiteSpace:'nowrap'}}>${fmtNum(tier.price)}<small style={{color:'#999',fontSize:11}}>/{t('张', 'image')}</small></span>
                                      </div>
                                    ))}
                                    {meta.input_image_price > 0 && (
                                      <div style={{fontSize:11, color:'#999', marginTop:2}}>
                                        {t('输入图首张免费，第 2 张起', 'First input image free, then')} ${fmtNum(meta.input_image_price)}/{t('张', 'image')}
                                      </div>
                                    )}
                                    <BillingRules model={model} lang={lang} />
                                  </div>
                                );
                              }
                              return <span className="price">${parseFloat(model.billing_rate || 0).toFixed(3)}<small>/{t('张', 'image')}</small></span>;
                            })()}
                            {model.billing_type === 'free' && (
                              <span className="price-free">Free</span>
                            )}
                          </td>
                          <td>
                            {model.is_hot ? <span style={{background:'#fff7e6',color:'#fa8c16',fontSize:11,padding:'2px 8px',borderRadius:10,border:'1px solid #ffd591',whiteSpace:'nowrap'}}>🔥 {t('热门', 'Hot')}</span> : <span style={{color:'#ccc'}}>-</span>}
                          </td>
                          <td>
                            <span className="description">{model.description || '-'}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}

            {models.length === 0 && (
              <div className="pricing-empty">
                {t('暂无模型数据', 'No model data available')}
              </div>
            )}
          </div>
        )}

        <div className="pricing-note">
          <h3>{t('计费说明', 'Billing Notes')}</h3>
          <ul>
            <li>{t('价格按每百万 tokens 展示，按实际使用的 tokens 数量计费', 'Prices are shown per million tokens, billed based on actual tokens used')}</li>
            <li>{t('输入和输出分别计费，按照各自的单价计算', 'Input and output are billed separately according to their respective unit prices')}</li>
            <li>{t('部分模型支持分时计费：高峰时段（工作日 9:00-12:00、14:00-18:00 北京时间）与空闲时段价格不同，以页面展示为准', 'Some models use time-based pricing: peak hours (weekdays 9:00-12:00, 14:00-18:00 Beijing time) differ from off-peak pricing as shown above')}</li>
            <li>{t('图片按每张成功输出的实际宽×高像素档位计费；图层拆分的底图与每个图层分别计费。点击模型旁的「计费规则」查看详细算法', 'Image outputs are billed by each successful output’s actual width × height tier; layer decomposition bills the base and every layer separately. Click “Billing rules” next to a model for the full formula')}</li>
            <li>{t('充值余额用于支付 API 调用费用，余额不足时将无法使用', 'Recharged balance is used to pay for API calls, service will be unavailable when balance is insufficient')}</li>
            <li>{t('如有批量采购需求，请联系商务合作', 'For bulk purchase needs, please contact business cooperation')}</li>
          </ul>
        </div>
      </main>

      <footer className="pricing-footer">
        <div className="landing-footer-bottom">
          <p>{footerCopyright}</p>
        </div>
      </footer>

      <style>{`
        .pricing-page {
          min-height: 100vh;
          background: #f5f5f5;
        }
        .pricing-nav {
          background: #fff;
          border-bottom: 1px solid #e5e7eb;
          padding: 12px 0;
        }
        .pricing-nav-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .pricing-logo {
          font-size: 20px;
          font-weight: 600;
          color: #1677ff;
          text-decoration: none;
        }
        .pricing-nav-links a {
          color: #6b7280;
          text-decoration: none;
          font-size: 14px;
        }
        .pricing-nav-links a:hover {
          color: #1677ff;
        }
        .pricing-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 32px 24px;
        }
        .pricing-header {
          text-align: center;
          margin-bottom: 40px;
        }
        .pricing-header h1 {
          font-size: 32px;
          font-weight: 600;
          color: #111827;
          margin-bottom: 12px;
        }
        .pricing-header p {
          font-size: 16px;
          color: #6b7280;
        }
        .pricing-loading,
        .pricing-empty {
          text-align: center;
          padding: 60px 0;
          color: #6b7280;
          font-size: 16px;
        }
        .pricing-groups {
          display: flex;
          flex-direction: column;
          gap: 32px;
        }
        .pricing-group {
          background: #fff;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .pricing-group h2 {
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 20px;
          padding-bottom: 12px;
          border-bottom: 2px solid #e5e7eb;
        }
        .pricing-table-wrapper {
          overflow-x: auto;
        }
        .pricing-table {
          width: 100%;
          border-collapse: collapse;
        }
        .pricing-table th,
        .pricing-table td {
          padding: 14px 16px;
          text-align: left;
          border-bottom: 1px solid #e5e7eb;
        }
        .pricing-table th {
          font-size: 13px;
          font-weight: 600;
          color: #6b7280;
          background: #f9fafb;
        }
        .pricing-table tbody tr:hover {
          background: #f9fafb;
        }
        .pricing-table tbody tr:last-child td {
          border-bottom: none;
        }
        .model-name {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .model-name span {
          font-weight: 500;
          color: #111827;
        }
        .model-name small {
          font-size: 12px;
          color: #9ca3af;
          font-family: monospace;
        }
        .hot-tag {
          margin-bottom: 4px;
        }
        .provider-name {
          font-size: 14px;
          color: #4b5563;
        }
        .price {
          font-size: 15px;
          font-weight: 600;
          color: #059669;
        }
        .price + small {
          display: block;
          font-size: 11px;
          color: #9ca3af;
        }
        .rule-pricing {
          display: flex;
          flex-direction: column;
          gap: 4px;
          font-size: 12px;
          line-height: 1.5;
          max-width: 340px;
        }
        .rule-pricing-item {
          padding: 3px 0;
        }
        .rule-pricing-label {
          font-weight: 600;
          color: #1677ff;
          margin-right: 6px;
        }
        .rule-pricing-window {
          color: #9ca3af;
        }
        .rule-pricing-prices {
          color: #4b5563;
          display: flex;
          flex-wrap: wrap;
          gap: 2px 10px;
        }
        .rule-pricing-hit {
          color: #52c41a;
        }
        .rule-pricing-vol {
          color: #9ca3af;
        }
        .description {
          font-size: 13px;
          color: #6b7280;
          max-width: 300px;
        }
        .price-free {
          font-size: 15px;
          font-weight: 600;
          color: #059669;
        }
        .pricing-note {
          margin-top: 32px;
          background: #fff;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .pricing-note h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 16px;
        }
        .pricing-note ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .pricing-note li {
          font-size: 14px;
          color: #4b5563;
          line-height: 1.8;
          padding: 6px 0;
          padding-left: 20px;
          position: relative;
        }
        .pricing-note li::before {
          content: '•';
          position: absolute;
          left: 0;
          color: #1677ff;
        }
        .pricing-footer {
          margin-top: 32px;
          background: #fff;
          border-top: 1px solid #e5e7eb;
        }
        .landing-footer-bottom {
          max-width: 1200px;
          margin: 0 auto;
          padding: 24px;
          text-align: center;
        }
        .landing-footer-bottom p {
          color: #9ca3af;
          font-size: 14px;
          margin: 0;
        }
        @media (max-width: 768px) {
          .pricing-content {
            padding: 20px 16px;
          }
          .pricing-header h1 {
            font-size: 24px;
          }
          .pricing-group {
            padding: 16px;
          }
          .pricing-table th,
          .pricing-table td {
            padding: 10px 12px;
          }
        }
      `}</style>
    </div>
  );
};

export default Pricing;
